import { useEffect, useState } from 'react';
import type { CountryCode } from 'libphonenumber-js/mobile';
import { Button, InlineNote, Sheet, sheetText, useNote } from '../components/ui';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app';
import { friendly } from '../lib/errors';
import { phoneDisplay } from '../lib/format';
import { useT } from '../lib/i18n';
import { countryOf, e164, formatNational, isEmail, maxDigits, startCountry } from '../lib/phone';
import { CountrySheet } from './Phone';

/**
 * Account → "Add email" / "Add phone": the new phone or email is proved with a code (POST
 * /v1/rider/me/identifiers/otp, then …/verify), then saved; the rider can sign in with either from then on.
 */
export function AddIdentifierSheet({ kind, open, onClose }: { kind: 'phone' | 'email'; open: boolean; onClose: () => void }) {
  const { api, setRider } = useApp();
  const t = useT();
  const [step, setStep] = useState<'enter' | 'code'>('enter');
  const [email, setEmail] = useState('');
  const [digits, setDigits] = useState('');
  const [country, setCountry] = useState<CountryCode>(() => startCountry());
  const [picking, setPicking] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, say] = useNote();
  const [devCode, setDevCode] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setStep('enter');
    setCode('');
    setDevCode(null);
    say(null);
  }, [open, say]);

  const phone = e164(digits, country);
  const to = kind === 'email' ? { email: email.trim().toLowerCase() } : { phone: phone ?? '' };
  const shown = kind === 'email' ? email.trim().toLowerCase() : phoneDisplay(phone ?? '');
  const c = countryOf(country);

  async function send() {
    if (kind === 'email' ? !isEmail(email) : !phone) return say(kind === 'email' ? t('email.invalid') : t('phone.invalid', { country: c.name }), 'error');
    setBusy(true);
    try {
      const r = await api.identifierOtp(to as { email: string } | { phone: string });
      setDevCode(import.meta.env.DEV ? (r.devCode ?? null) : null);
      setStep('code');
      say(null);
    } catch (e) {
      say(friendly(e, t), 'error');
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    if (!/^\d{4,8}$/.test(code)) return say(t('err.OTP_WRONG'), 'error');
    setBusy(true);
    try {
      setRider(await api.identifierVerify(to as { email: string } | { phone: string }, code));
      onClose();
    } catch (e) {
      say(friendly(e, t), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Sheet open={open && !picking} onClose={onClose} title={kind === 'email' ? t('acc.addEmail') : t('acc.addPhone')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void (step === 'enter' ? send() : verify());
          }}
        >
          {step === 'enter' ? (
            <>
              <p style={sheetText}>{kind === 'email' ? t('acc.emailBody') : t('acc.phoneBody')}</p>
              {kind === 'email' ? (
                <input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" aria-label={t('email.label')} className="zr-field" style={{ marginTop: 16, fontSize: 17, fontWeight: 700 }} />
              ) : (
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setPicking(true)} aria-label={t('phone.countryAria', { name: c.name, dial: c.dial })} className="zr-chip" style={{ height: 56, borderRadius: 18 }}>
                    <span aria-hidden="true" style={{ fontSize: 20 }}>{c.flag}</span>
                    <span className="zr-num">{c.dial}</span>
                    <Icon name="chevronDown" size={16} sw={2.2} />
                  </button>
                  <input type="tel" inputMode="tel" autoComplete="tel-national" value={formatNational(digits, country)} onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, maxDigits(country)))} aria-label={t('phone.label')} className="zr-field" style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700 }} />
                </div>
              )}
            </>
          ) : (
            <>
              <p style={sheetText}>{t('acc.codeSent', { to: shown })}</p>
              {devCode && <p style={{ ...sheetText, color: 'var(--acText)' }}>{t('code.dev')} · {devCode}</p>}
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label={t('code.title')} placeholder="••••••" className="zr-field" style={{ marginTop: 16, fontSize: 22, fontWeight: 700, letterSpacing: '0.2em', fontVariantNumeric: 'tabular-nums' }} />
            </>
          )}
          <InlineNote note={note} />
          <Button type="submit" busy={busy} style={{ marginTop: 16 }}>
            {step === 'enter' ? t('phone.send') : t('acc.verify')}
          </Button>
        </form>
      </Sheet>
      <CountrySheet
        open={open && picking}
        onClose={() => setPicking(false)}
        value={country}
        only={null}
        onPick={(code2) => {
          setCountry(code2);
          setPicking(false);
        }}
      />
    </>
  );
}
